import React, { useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// Utils
import { isOwner, canEditPermissions } from '../../../../../../../../common/permissions/permissionUtil';
import { userIsRegistered } from '../../../../../../../../common/users/userHelper';
import { getElementId } from '../../../../../../../../common/elements/utils/elementPropertyUtils';
import { getUserEntryName } from '../editorPermissionPopup/boardEditorPermissionUtils';

// Components
import StyledButton from '../../../../../../../components/buttons/StyledButton';
import MobileBoardEditorPermissionSheetMenu from './MobileBoardEditorPermissionSheetMenu';
import MobileBoardPermissionSheetInfo from './MobileBoardPermissionSheetInfo';

// Selectors
import {
    currentBoardHasPublicEditPermissionsSelector,
    currentBoardUserPermissionsSelector,
} from '../../../../../../../utils/permissions/elementPermissionsSelector';

// Actions
import {
    removeUsersFromBoard,
    transferBoardOwnership,
    updateUserPermissionsForBoard,
} from '../../../../../../sharing/boardSharingActions';
import { navigateToBoard } from '../../../../../../../reducers/navigationActions';
import { closeSheet } from '../../../../../../../mobile/structural/sheet/sheetActions';

// Constants
import { ACCESS_BITS } from '../../../../../../../../common/permissions/permissionBitsConstants';
import { PERMISSION_VALUES } from '../../../../../../../../common/permissions/permissionConstants';

// Types
import { MNUser } from '../../../../../../../../common/users/userModelTypes';
import { MNElement } from '../../../../../../../../common/elements/elementModelTypes';
import { SheetId } from '../../../../../../../mobile/structural/sheet/sheetTypes';

// Styles
import './MobileBoardEditorPermissionSheet.scss';

/**
 * ConfirmationPanel component for user actions that need confirmation
 */
type ConfirmationPanelProps = {
    confirmationMessage: string;
    onConfirmation: () => void;
    onCancel: () => void;
    show: boolean;
};

const ConfirmationPanel = ({ confirmationMessage, onConfirmation, onCancel, show }: ConfirmationPanelProps) => {
    return (
        <div className={`confirmation-panel ${show ? 'show' : ''}`}>
            <div className="confirmation-message">{confirmationMessage}</div>
            <div className="confirmation-actions">
                <StyledButton className="primary thin" onClick={onConfirmation}>
                    Yes
                </StyledButton>
                <StyledButton className="secondary thin" onClick={onCancel}>
                    No
                </StyledButton>
            </div>
        </div>
    );
};

type MobileBoardEditorPermissionSheetContentProps = {
    permission: number;
    user: MNUser;
    editable: boolean;
    currentBoardEditors: {
        sharedBoard: MNElement;
        userId: string;
        user: MNUser;
    }[];
    userId: string;
    isCurrentUser: boolean;
    dispatchCloseSheet: () => void;
    dispatchUpdateActiveSnapPoint: (activeSnapPoint: number) => void;
    activeSnapPoint: number;
};

const MobileBoardEditorPermissionSheetContent = (props: MobileBoardEditorPermissionSheetContentProps) => {
    const {
        permission,
        user,
        editable,
        currentBoardEditors,
        userId,
        isCurrentUser,
        dispatchCloseSheet,
        dispatchUpdateActiveSnapPoint,
        activeSnapPoint,
    } = props;

    // Add state for confirmation
    const [confirmationState, setConfirmationState] = useState<{
        show: boolean;
        onConfirmAction: (() => void) | null;
        message: string;
    }>({
        show: false,
        onConfirmAction: null,
        message: '',
    });

    const name = getUserEntryName({ user, isCurrentUser });

    const userAccess = currentBoardEditors.find((entry) => entry.userId === userId);

    const userPermissions = useSelector(currentBoardUserPermissionsSelector);
    const hasPublicEditPermissions = useSelector(currentBoardHasPublicEditPermissionsSelector);

    const sharedBoard = userAccess?.sharedBoard;
    const sharedBoardId = getElementId(sharedBoard);

    const dispatch = useDispatch();

    const closeMobileSharingSheet = useCallback(() => dispatch(closeSheet(SheetId.Sharing)), [dispatch]);

    const dispatchNavigateToSharedBoard = useCallback(
        () => dispatch(navigateToBoard({ boardId: sharedBoardId })),
        [dispatch, sharedBoardId],
    );

    const removeSharedUser = () => {
        if (!userAccess) return;

        const shouldBlockUser = hasPublicEditPermissions && userIsRegistered(userAccess.user);

        if (shouldBlockUser) {
            return dispatch(
                updateUserPermissionsForBoard({
                    updateUserIds: [userAccess.userId],
                    boardId: getElementId(sharedBoard),
                    permissions: ACCESS_BITS.BLOCKED,
                }),
            );
        }

        dispatch(
            removeUsersFromBoard({
                removeUserIds: [userAccess.userId],
                boardId: sharedBoardId,
            }),
        );
    };

    const setUserPermissionsFull = () => {
        dispatch(
            updateUserPermissionsForBoard({
                updateUserIds: [userAccess.userId],
                boardId: getElementId(sharedBoard),
                permissions: PERMISSION_VALUES.FULL_ACCESS,
            }),
        );
    };

    const dispatchTransferBoardOwnership = () => {
        if (!userAccess) return;

        dispatch(
            transferBoardOwnership({
                newOwnerId: userAccess.userId,
                boardId: sharedBoardId,
            }),
        );
    };

    // Here we are updating the snap point to 0 before showing the confirmation
    // and then restoring it after the confirmation is shown to make it look
    // like the sheet closed and then re-opened with the confirmation message
    const showConfirmation = (message: string, onConfirmAction: () => void) => {
        dispatchUpdateActiveSnapPoint(0);

        setTimeout(() => {
            setConfirmationState({
                show: true,
                onConfirmAction,
                message,
            });

            dispatchUpdateActiveSnapPoint(activeSnapPoint);
        }, 300);
    };

    const cancelConfirmation = useCallback(() => {
        setConfirmationState({
            show: false,
            onConfirmAction: null,
            message: '',
        });
        dispatchCloseSheet();
    }, [dispatchCloseSheet]);

    const handleConfirmation = () => {
        if (confirmationState.onConfirmAction) {
            confirmationState.onConfirmAction();
        }
        cancelConfirmation();
    };

    // Determine what content to render based on conditions
    if (confirmationState.show) {
        return (
            <ConfirmationPanel
                confirmationMessage={confirmationState.message}
                onConfirmation={handleConfirmation}
                onCancel={cancelConfirmation}
                show={confirmationState.show}
            />
        );
    }

    const hasNoPermissionToEdit = !canEditPermissions(userPermissions);
    const isOwnerUser = isOwner(permission);
    const needsToNavigateToBoard = !editable;

    // If any condition is true, we should show the permission message
    if (hasNoPermissionToEdit || isOwnerUser || needsToNavigateToBoard) {
        return (
            <MobileBoardPermissionSheetInfo
                editable={editable}
                permission={permission}
                sharedBoard={sharedBoard}
                user={user}
                userPermissions={userPermissions}
                navigateToSharedBoard={dispatchNavigateToSharedBoard}
                dispatchCloseSheet={dispatchCloseSheet}
                closeMobileSharingSheet={closeMobileSharingSheet}
            />
        );
    }

    return (
        <MobileBoardEditorPermissionSheetMenu
            name={name}
            userPermissions={userPermissions}
            permission={permission}
            user={user}
            dispatchTransferBoardOwnership={dispatchTransferBoardOwnership}
            setUserPermissionsFull={setUserPermissionsFull}
            removeSharedUser={removeSharedUser}
            onShowConfirmation={showConfirmation}
        />
    );
};

export default MobileBoardEditorPermissionSheetContent;
